import {
  ChatComposer,
  type ChatComposerSubmitMethod,
  type ChatComposerValueChangeReason,
} from '@github-ui/agent-sessions-interface/components/ChatComposer'
import {SUBMIT_ERROR_MESSAGE} from '@github-ui/ahp-session-client/submission-error'
import {announce} from '@github-ui/aria-live'
import {ChatInputValidationError} from '@github-ui/copilot-chat-state/validation-error'
import {PrettifiedBlocksList} from '@github-ui/copilot-code-prettify/composer/PrettifiedBlocksList'
import {usePrettifyComposer} from '@github-ui/copilot-code-prettify/composer/use-prettify-composer'
import {RepoPicker} from '@github-ui/agent-sessions-interface/components/RepoPicker'
import {createScopedRepositoryLoader} from '@github-ui/agent-sessions-interface/components/repo-picker/scope'
import {MAX_AGENT_TASK_PROMPT_LENGTH} from '@github-ui/copilot-shared-ui/composer/max-prompt-length'
import {AttachmentErrorBanner} from '@github-ui/copilot-shared-ui/components/AttachmentErrorBanner'
import {ChatReferenceAutocomplete} from '@github-ui/copilot-shared-ui/components/ChatReferenceAutocomplete'
import {FilePickerPanel} from '@github-ui/copilot-shared-ui/components/FilePickerPanel'
import {fetchRepos} from '@github-ui/copilot-shared-ui/repos/fetch-repos'
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ClipboardEvent,
  type ReactNode,
  type RefObject,
} from 'react'

import {SpacePicker} from '../spaces/SpacePicker'
import {EmptyState, suggestionContextForAttachment} from '../existing-session/EmptyState'
import {
  SELECTED_SPACE_ATTACHMENT_ID,
  hasSubmittableAttachments,
  selectedSpaceAttachment,
} from '../spaces/space-attachment'
import {EnvironmentAttachmentControls, EnvironmentImageAttachments} from './EnvironmentAttachments'
import {ComposerToolbar} from './ComposerToolbar'
import {useComposerFileAttach} from '@github-ui/copilot-shared-ui/hooks/use-composer-file-attach'
import {
  useComposerReferenceActivation,
  type ComposerActivationTarget,
} from '@github-ui/copilot-shared-ui/hooks/use-composer-reference-activation'
import {attachmentRepoPickerScopeSubtitle} from './shared'
import {isImplicitChipId} from './use-implicit-draft-attachments'
import type {TaskComposerController} from './use-task-composer'
import {INJECTED_ATTACHMENT_PREFIX} from '@github-ui/copilot-shared-ui/contexts/InjectedReferencesContext'
import {useOverlayDirection} from '@github-ui/copilot-chat-contexts/OverlayDirectionContext'
import styles from './CopilotComposerInput.module.css'
import {useUiReadinessWitness} from '../shared/use-ui-readiness-witness'
import {
  clientInteractionFailed,
  clientInteractionForEvent,
} from '@github-ui/copilot-browser-latency/copilot-client-interaction'
import {useCopilotClientInteractions} from '@github-ui/copilot-browser-latency/use-copilot-client-interactions'
import {useComposerFocusTelemetry, useComposerTelemetry} from '../telemetry/ComposerTelemetryContext'
import {instrumentSubmit} from '../telemetry/composer-telemetry'
import {toTelemetryAttachments} from '../telemetry/attachments'
import type {ComposerAttachmentVia, ComposerSubmitMethod} from '../telemetry/constants'
import {clsx} from 'clsx'

const CHAT_PLACEHOLDER = 'Ask anything. Type @ to attach references'

export interface CopilotComposerInputProps {
  controller: TaskComposerController
  restorePrettifiedBlocks?: boolean
  value: string
  onValueChange: (value: string | ((current: string) => string), reason?: ChatComposerValueChangeReason) => void
  textAreaRef: RefObject<HTMLTextAreaElement | null>
  disabled: boolean
  readOnly: boolean
  readinessStatePending?: boolean
  readinessUserActionPending?: boolean
  emptyState?: 'embedded'
  containerClassName?: string
  submitWithModifier: boolean
  clearOnSubmit: boolean
  disableWhileSubmitting: boolean
  maxTextareaHeight?: number
  /**
   * The premium-request meter, supplied by the owner that knows whether the
   * draft's environment draws on that quota. Omitted where it does not apply.
   */
  meter?: ReactNode
  leadingControl?: ReactNode
  /** Opts the toolbar pickers into the composer's container-query compaction. */
  responsiveIconOnly?: boolean
  disableAutoFocus?: boolean
  /** Content that should remain between the composer and its feedback banners. */
  belowComposerContent?: ReactNode
  onBeforeSubmit?: () => void
  onInteraction?: () => void
}

/**
 * The composing surface for a coding task: the environment's attachments,
 * image handling, repo picker, and the task toolbar.
 *
 * Everything it shows is driven by the task composer controller, which the new
 * session composer owns — the chat and task surfaces share one draft, so this
 * takes the controller rather than building its own.
 */
const NO_IMAGE_REFERENCES: TaskComposerController['imageReferences'] = []

export function CopilotComposerInput({
  controller,
  restorePrettifiedBlocks,
  value,
  onValueChange,
  textAreaRef,
  disabled,
  readOnly,
  readinessStatePending,
  readinessUserActionPending,
  emptyState,
  containerClassName,
  submitWithModifier,
  clearOnSubmit,
  disableWhileSubmitting,
  maxTextareaHeight,
  meter,
  leadingControl,
  responsiveIconOnly,
  disableAutoFocus,
  belowComposerContent,
  onBeforeSubmit,
  onInteraction,
}: CopilotComposerInputProps) {
  const onReadiness = useUiReadinessWitness({
    kind: 'new',
    readOnly,
    statePending: readinessStatePending,
    userActionPending: readinessUserActionPending,
  })
  const interactionObserver = useCopilotClientInteractions({role: readOnly ? 'read_only' : 'writable'})
  const preferredOverlaySide = useOverlayDirection()
  const tooltipDirection =
    preferredOverlaySide === 'outside-top' || preferredOverlaySide === 'inside-top' ? 'n' : undefined
  const {
    onSubmit: submit,
    onSubmitInteraction: submitInteraction,
    isAhpEnvironment,
    isSandboxDraft,
    environmentAttachments,
    implicitAttachments = [],
    attachmentScopeOwner,
    pickerScopeOwner,
    implicitChips,
    dismissImplicitChip,
    injectedReferences,
    removeInjectedReference,
    attachReferenceUrl,
    onPaste,
    dropTargetProps,
    isDraggedOver,
    targetSelection,
    selectedSpace,
    setSelectedSpace,
    isSpaceResolving,
    setIsSpaceResolving,
    spaceSelectionError,
    setSpaceSelectionError,
  } = controller
  const telemetry = useComposerTelemetry()
  const {
    blocks,
    hasBlocks,
    handlePaste: handlePrettifyPaste,
    popLastBlock,
    serialize,
    removeSubmittedBlocks,
    onRemoveBlock,
    onToggleCollapse,
    onCodeChange,
  } = usePrettifyComposer({
    draftKey: `task:${controller.draftThreadId}`,
    restore: restorePrettifiedBlocks,
  })
  const isChat = targetSelection.kind === 'chat'
  useComposerFileAttach(environmentAttachments, isAhpEnvironment && !readOnly && !disabled)
  const scopedRepoLoader = useMemo(
    () =>
      createScopedRepositoryLoader((query, signal) => fetchRepos(query, signal, pickerScopeOwner), pickerScopeOwner),
    [pickerScopeOwner],
  )
  const fileInputRef = useRef<HTMLInputElement>(null)
  const attachButtonRef = useRef<HTMLButtonElement>(null)
  const heldKeyRef = useRef<string | null>(null)
  const suppressedKeyRef = useRef<string | null>(null)
  const blockSubmitInFlightRef = useRef(false)
  const [submissionError, setSubmissionError] = useState<string>()
  const openUploadDialog = useCallback(() => fileInputRef.current?.click(), [])
  // The selected Space (a display-only scope indicator) and the page the draft
  // was composed on are shown beside what the user attached by hand, so the
  // first turn says what it will carry before it is sent. The Space picker lives
  // inside the attach ("+") menu, which closes on selection and unmounts its
  // label, so the token keeps the chosen Space visible while composing.
  const spaceToken = useMemo(
    () => (isChat ? selectedSpaceAttachment(selectedSpace) : undefined),
    [isChat, selectedSpace],
  )
  const composerAttachments = useMemo(
    () => [
      ...(spaceToken ? [spaceToken] : []),
      ...(isAhpEnvironment ? environmentAttachments.attachments : []),
      ...implicitChips,
      ...injectedReferences.map(item => item.token),
    ],
    [spaceToken, environmentAttachments.attachments, implicitChips, injectedReferences, isAhpEnvironment],
  )
  const handleRemoveAttachment = useCallback(
    (id: string) => {
      if (id === SELECTED_SPACE_ATTACHMENT_ID) {
        telemetry?.track('space_cleared', {via: 'chip'})
        setSelectedSpace(undefined)
        return
      }
      if (isImplicitChipId(id)) {
        dismissImplicitChip(id)
        return
      }
      if (id.startsWith(INJECTED_ATTACHMENT_PREFIX)) {
        removeInjectedReference(id)
        return
      }
      environmentAttachments.handleRemoveAttachment(id)
    },
    [dismissImplicitChip, environmentAttachments, removeInjectedReference, setSelectedSpace, telemetry],
  )
  const activationTargets = useMemo<ComposerActivationTarget[]>(
    () => [
      ...implicitChips.flatMap((chip, index) => {
        const attachment = implicitAttachments[index]
        return attachment ? [{id: chip.id, target: {kind: 'attachment' as const, attachment}}] : []
      }),
      ...injectedReferences.map(item => ({
        id: item.token.id,
        target: {kind: 'attachment' as const, attachment: item.ahp, id: item.token.id},
      })),
      ...(selectedSpace
        ? [
            {
              id: SELECTED_SPACE_ATTACHMENT_ID,
              target: {kind: 'space' as const, owner: selectedSpace.owner, number: selectedSpace.id},
            },
          ]
        : []),
    ],
    [implicitAttachments, implicitChips, injectedReferences, selectedSpace],
  )
  const referenceActivation = useComposerReferenceActivation(
    environmentAttachments,
    composerAttachments,
    activationTargets,
  )
  const submitWithMethod = useCallback(
    async (content: string, method: ComposerSubmitMethod, event?: Event) => {
      if (blockSubmitInFlightRef.current) {
        clientInteractionFailed(clientInteractionForEvent(event))
        return false
      }
      const isBlockSubmission = blocks.length > 0
      if (isBlockSubmission) {
        blockSubmitInFlightRef.current = true
      }
      setSubmissionError(undefined)
      try {
        const submittedBlocks = new Map(blocks.map(block => [block.id, block.code] as const))
        const prompt = serialize(content)
        const succeeded =
          (await instrumentSubmit(telemetry, method, prompt.length, () => {
            if (prompt.length > MAX_AGENT_TASK_PROMPT_LENGTH) {
              throw new ChatInputValidationError('Prompt is too long. Shorten the pasted content or your message.')
            }
            onBeforeSubmit?.()
            return event && submitInteraction ? submitInteraction(event, prompt) : submit(prompt)
          })) !== false
        if (succeeded) removeSubmittedBlocks(submittedBlocks)
        return succeeded
      } catch (error) {
        clientInteractionFailed(clientInteractionForEvent(event))
        const message = error instanceof ChatInputValidationError ? error.message : SUBMIT_ERROR_MESSAGE
        setSubmissionError(message)
        announce(message)
        return false
      } finally {
        if (isBlockSubmission) blockSubmitInFlightRef.current = false
      }
    },
    [blocks, onBeforeSubmit, removeSubmittedBlocks, serialize, submit, submitInteraction, telemetry],
  )
  const submitGestureRef = useRef<ChatComposerSubmitMethod | null>(null)
  const onSubmitGesture = useCallback((method: ChatComposerSubmitMethod) => {
    submitGestureRef.current = method
  }, [])
  const onSubmit = useCallback(
    (content: string) => {
      const method = submitGestureRef.current ?? 'unknown'
      submitGestureRef.current = null
      return submitWithMethod(content, method)
    },
    [submitWithMethod],
  )
  const onSubmitInteraction = useCallback(
    (content: string, event?: Event) => {
      const method = submitGestureRef.current ?? 'unknown'
      submitGestureRef.current = null
      return submitWithMethod(content, method, event)
    },
    [submitWithMethod],
  )

  // Telemetry only. None of these handlers change composer behavior; they wrap
  // the existing callbacks to record which control was used. Text is reduced to
  // its length before it reaches the tracker.
  const onFocusCapture = useComposerFocusTelemetry()
  const userEditPendingRef = useRef(false)
  const handleValueChange = useCallback<typeof onValueChange>(
    (update, reason) => {
      if ((reason ?? 'input') === 'input') userEditPendingRef.current = true
      onValueChange(update, reason)
    },
    [onValueChange],
  )
  useEffect(() => {
    if (!telemetry) return
    if (userEditPendingRef.current) {
      userEditPendingRef.current = false
      telemetry.textChanged(value.length)
    } else {
      telemetry.textBaseline(value.length)
    }
  }, [telemetry, value])
  const basePaste = isAhpEnvironment ? environmentAttachments.handlePaste : onPaste
  const handlePaste = useCallback(
    (event: ClipboardEvent<HTMLTextAreaElement>) => {
      const fileCount = event.clipboardData?.files?.length ?? 0
      try {
        if (fileCount > 0) telemetry?.paste('files', 0)
        else telemetry?.paste('text', event.clipboardData?.getData('text/plain').length ?? 0)
      } catch {
        // Telemetry must never block the paste.
      }
      const pastedText = event.clipboardData?.getData('text/plain') ?? ''
      const maxRun = (pastedText.match(/`+/g) ?? []).reduce((max, run) => Math.max(max, run.length), 0)
      const fenceLength = Math.max(3, maxRun + 1)
      const prompt = serialize(value)
      const pasteOverhead = fenceLength * 2 + 2 + (prompt ? 1 : 0)
      if (fileCount === 0 && prompt.length + pastedText.length + pasteOverhead <= MAX_AGENT_TASK_PROMPT_LENGTH) {
        handlePrettifyPaste(event)
      }
      if (!event.defaultPrevented) basePaste?.(event)
    },
    [basePaste, handlePrettifyPaste, serialize, telemetry, value],
  )
  const handleUploadFiles = useCallback(() => {
    telemetry?.noteAttachmentVia('attach_menu')
    openUploadDialog()
  }, [openUploadDialog, telemetry])
  const attachHandlers = useMemo(() => {
    const withVia =
      <Args extends unknown[], R>(via: ComposerAttachmentVia, handler: (...args: Args) => R) =>
      (...args: Args): R => {
        telemetry?.noteAttachmentVia(via)
        return handler(...args)
      }
    return {
      onFilesAdded: withVia('paste_or_drop', environmentAttachments.handleFilesAdded),
      onAttachFiles: withVia('attach_menu', environmentAttachments.openFilePicker),
      onAttachRepo: withVia('attach_menu', environmentAttachments.openRepoPicker),
      onMentionRepos: withVia('mention', environmentAttachments.handleUpdateRepos),
      onMentionFiles: withVia('mention', environmentAttachments.handleFilePickerSelect),
    }
  }, [
    environmentAttachments.handleFilePickerSelect,
    environmentAttachments.handleFilesAdded,
    environmentAttachments.handleUpdateRepos,
    environmentAttachments.openFilePicker,
    environmentAttachments.openRepoPicker,
    telemetry,
  ])
  // Inline markdown images only count once uploaded (their GitHub asset URL is in the prompt). Ids stay local.
  const inlineImageReferences = controller.imageReferences ?? NO_IMAGE_REFERENCES
  const telemetryAttachments = useMemo(
    () =>
      toTelemetryAttachments(
        composerAttachments,
        isAhpEnvironment
          ? environmentAttachments.images.map(image => image.id)
          : inlineImageReferences.map(image => image.id),
      ),
    [composerAttachments, environmentAttachments.images, inlineImageReferences, isAhpEnvironment],
  )
  useEffect(() => {
    telemetry?.attachmentsChanged(telemetryAttachments)
  }, [telemetry, telemetryAttachments])
  const latestExplicitAttachment = injectedReferences.at(-1)?.ahp ?? environmentAttachments.tracked?.at(-1)?.ahp
  const suggestionContext = latestExplicitAttachment
    ? suggestionContextForAttachment(latestExplicitAttachment)
    : (environmentAttachments.selectedRepos?.length ?? 0) > 0
      ? 'repository'
      : suggestionContextForAttachment(implicitAttachments.at(-1))

  const imagePreview =
    isAhpEnvironment && environmentAttachments.images.length > 0 ? (
      <EnvironmentImageAttachments controller={environmentAttachments} />
    ) : undefined

  return (
    <>
      {emptyState === 'embedded' && !readOnly && !disabled ? (
        <EmptyState
          suggestionContext={suggestionContext}
          onCommit={interactionObserver ? event => interactionObserver.command('send', event) : undefined}
          onSelectSuggestion={async (suggestion, event) => {
            telemetry?.track('suggestion_selected', {suggestion_context: suggestionContext})
            await submitWithMethod((suggestion.prompt ?? suggestion.question).trim(), 'suggestion', event)
          }}
        />
      ) : null}
      <div>
        <div
          {...dropTargetProps}
          onDropCapture={() => telemetry?.noteAttachmentVia('paste_or_drop')}
          onFocusCapture={onFocusCapture}
          onClickCapture={onInteraction}
          onKeyDownCapture={event => {
            onInteraction?.()
            heldKeyRef.current = event.code
            if (event.repeat && event.code === suppressedKeyRef.current) {
              event.preventDefault()
              event.stopPropagation()
            }
          }}
          onKeyUpCapture={event => {
            if (event.code === heldKeyRef.current) heldKeyRef.current = null
            if (event.code === suppressedKeyRef.current) suppressedKeyRef.current = null
          }}
        >
          <ChatComposer
            onReadiness={onReadiness}
            interactionObserver={interactionObserver}
            value={value}
            onValueChange={handleValueChange}
            onInputMutation={onInteraction}
            textAreaRef={textAreaRef}
            onSubmit={onSubmit}
            onSubmitInteraction={onSubmitInteraction}
            onSubmitGesture={onSubmitGesture}
            placeholder={CHAT_PLACEHOLDER}
            ariaLabel={isChat ? 'Send a message' : 'Coding task prompt'}
            containerClassName={clsx(containerClassName, isDraggedOver && styles.isDraggedOver)}
            inputTextSize="responsive"
            submitWithModifier={submitWithModifier}
            clearOnSubmit={clearOnSubmit}
            disableWhileSubmitting={disableWhileSubmitting || isAhpEnvironment || hasBlocks}
            maxLength={MAX_AGENT_TASK_PROMPT_LENGTH}
            maxTextareaHeight={maxTextareaHeight}
            tooltipDirection={tooltipDirection}
            disableAutoFocus={disableAutoFocus}
            onPaste={handlePaste}
            aboveInput={
              hasBlocks ? (
                <PrettifiedBlocksList
                  blocks={blocks}
                  onRemove={id => {
                    suppressedKeyRef.current = heldKeyRef.current
                    onRemoveBlock(id)
                    textAreaRef.current?.focus()
                  }}
                  onToggleCollapse={onToggleCollapse}
                  onCodeChange={onCodeChange}
                  readOnly={readOnly}
                />
              ) : null
            }
            onEmptyBackspace={
              !readOnly && hasBlocks
                ? () => {
                    if (blockSubmitInFlightRef.current || textAreaRef.current?.readOnly) return
                    suppressedKeyRef.current = heldKeyRef.current
                    popLastBlock()
                  }
                : undefined
            }
            attachments={referenceActivation.attachments}
            hasAttachedContent={Boolean(
              hasBlocks ||
              hasSubmittableAttachments(composerAttachments) ||
              (isAhpEnvironment && environmentAttachments.hasAttachments),
            )}
            {...(isAhpEnvironment
              ? {
                  onFilesAdded: attachHandlers.onFilesAdded,
                  onAttachFiles: attachHandlers.onAttachFiles,
                  onUploadFiles: handleUploadFiles,
                  onAttachRepo: attachHandlers.onAttachRepo,
                  attachButtonRef,
                  ...(imagePreview ? {attachmentPreview: imagePreview} : {}),
                }
              : {})}
            attachMenuContent={
              isAhpEnvironment ? (
                <SpacePicker
                  selectedSpace={selectedSpace}
                  onSelectSpace={setSelectedSpace}
                  isResolving={isSpaceResolving}
                  onResolvingChange={setIsSpaceResolving}
                  selectionError={spaceSelectionError}
                  onSelectionErrorChange={setSpaceSelectionError}
                  disabled={!isSandboxDraft}
                  disabledDescription="Spaces can only be attached when starting a new cloud session."
                />
              ) : undefined
            }
            onRemoveAttachment={handleRemoveAttachment}
            onAttachmentClick={referenceActivation.onAttachmentClick}
            renderInput={textarea => (
              <ChatReferenceAutocomplete
                disabled={!isAhpEnvironment || readOnly}
                scopeOwner={pickerScopeOwner}
                repositoryLoader={scopedRepoLoader}
                selectedRepos={environmentAttachments.selectedRepos}
                onUpdateRepos={attachHandlers.onMentionRepos}
                onFilePickerSelect={attachHandlers.onMentionFiles}
                attachReferenceUrl={attachReferenceUrl}
                restrictionOwner={attachmentScopeOwner ?? undefined}
              >
                {textarea}
              </ChatReferenceAutocomplete>
            )}
            disabled={disabled}
            readOnly={readOnly}
            toolbarContent={
              readOnly ? undefined : (
                <ComposerToolbar
                  controller={controller}
                  leadingControl={leadingControl}
                  responsiveIconOnly={responsiveIconOnly}
                  tooltipDirection={tooltipDirection}
                />
              )
            }
            trailingToolbarContent={readOnly ? undefined : meter}
          />
        </div>
        {belowComposerContent}
      </div>
      {submissionError && (
        <AttachmentErrorBanner
          messages={[submissionError]}
          errorCode="message_submit_failed"
          onDismiss={() => setSubmissionError(undefined)}
          title="Submission error"
        />
      )}
      {isAhpEnvironment && (
        <>
          <AttachmentErrorBanner
            messages={environmentAttachments.rejectionMessages}
            errorCode="attachment_rejected"
            onDismiss={environmentAttachments.dismissRejections}
          />
          <EnvironmentAttachmentControls controller={environmentAttachments} fileInputRef={fileInputRef} />
          {environmentAttachments.filePickerOpen && (
            <FilePickerPanel
              key={`files:${pickerScopeOwner ?? 'unresolved'}`}
              isOpen
              onClose={environmentAttachments.closeFilePicker}
              onSelect={environmentAttachments.handleFilePickerSelect}
              repositoryLoader={scopedRepoLoader}
              scopeOwner={pickerScopeOwner}
            />
          )}
          <RepoPicker
            key={`repos:${pickerScopeOwner ?? 'unresolved'}`}
            anchorRef={attachButtonRef}
            repositoryLoader={scopedRepoLoader}
            selectedRepos={environmentAttachments.selectedRepos}
            onUpdateRepos={environmentAttachments.handleUpdateRepos}
            triggerOpen={environmentAttachments.repoPickerOpen}
            onClose={environmentAttachments.closeRepoPicker}
            anchorSide="outside-top"
            subtitle={attachmentRepoPickerScopeSubtitle(pickerScopeOwner, controller.resolvedUserLogin)}
            restrictionOwner={controller.scopeOwner ?? undefined}
          />
        </>
      )}
    </>
  )
}

CopilotComposerInput.displayName = 'CopilotComposerInput'
